import { useCallback, useEffect, useRef, useState } from 'react';
import Notification from 'components/notification';
import {
  ICustomContentItem,
  ICustomContentListParams,
  ICustomContentListResponse,
  getCustomContentList,
} from 'services/customContent';
import { FEATURE_FLAGS, useBooleanFlag } from './useFeatureFlags';

interface ICustomContentPagination {
  page: number;
  page_size: number;
  count: number;
  next: string | null;
  previous: string | null;
}

interface IUseCustomContentList {
  items: ICustomContentItem[];
  pagination: ICustomContentPagination;
  fetching: boolean;
  hasFetchError: boolean;
  getContentList: (params?: ICustomContentListParams) => Promise<ICustomContentListResponse | null>;
  getNextPage: () => Promise<ICustomContentListResponse | null>;
  getPreviousPage: () => Promise<ICustomContentListResponse | null>;
  getPage: (page: number, page_size?: number) => Promise<ICustomContentListResponse | null>;
}

const INITIAL_PAGE = 1;
const INITIAL_PAGE_SIZE = 10;

const initialPagination: ICustomContentPagination = {
  page: INITIAL_PAGE,
  page_size: INITIAL_PAGE_SIZE,
  count: 0,
  next: null,
  previous: null,
};

const toPositiveNumber = (value: number | string | null | undefined, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const getPaginationParamsFromUrl = (
  url: string | null
): { page: number | null; page_size: number | null } => {
  if (!url) {
    return { page: null, page_size: null };
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    const page = toPositiveNumber(parsedUrl.searchParams.get('page'), 0);
    const page_size = toPositiveNumber(parsedUrl.searchParams.get('page_size'), 0);

    return {
      page: page > 0 ? page : null,
      page_size: page_size > 0 ? page_size : null,
    };
  } catch (_error) {
    return { page: null, page_size: null };
  }
};

const useCustomContentList = (): IUseCustomContentList => {
  const customContentEnabled = useBooleanFlag(FEATURE_FLAGS.CUSTOM_CONTENT_ENABLED);
  const isCustomContentEnabled = Boolean(customContentEnabled);
  const [items, setItems] = useState<ICustomContentItem[]>([]);
  const [pagination, setPagination] = useState<ICustomContentPagination>(initialPagination);
  const [fetching, setFetching] = useState(false);
  const [hasFetchError, setHasFetchError] = useState(false);
  const paginationRef = useRef(initialPagination);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const getContentList = useCallback(
    async (params: ICustomContentListParams = {}) => {
      if (!isCustomContentEnabled) {
        setItems([]);
        setPagination(initialPagination);
        setHasFetchError(false);
        return null;
      }

      const fallbackPagination = paginationRef.current;
      const page = toPositiveNumber(params.page, fallbackPagination.page);
      const page_size = toPositiveNumber(params.page_size, fallbackPagination.page_size);

      setFetching(true);
      setHasFetchError(false);
      try {
        const { data } = await getCustomContentList({ ...params, page, page_size });
        const normalizedResults = Array.isArray(data?.results) ? data.results : [];
        const normalizedCount = toPositiveNumber(data?.count, 0);
        const normalizedNext = data?.next || null;
        const normalizedPrevious = data?.previous || null;

        setItems(normalizedResults);
        setPagination({
          page,
          page_size,
          count: normalizedCount,
          next: normalizedNext,
          previous: normalizedPrevious,
        });
        setHasFetchError(false);

        return {
          count: normalizedCount,
          next: normalizedNext,
          previous: normalizedPrevious,
          results: normalizedResults,
        };
      } catch (error) {
        Notification({ text: 'There has been a problem fetching custom content', type: 'error' });
        setHasFetchError(true);
        return null;
      } finally {
        setFetching(false);
      }
    },
    [isCustomContentEnabled]
  );

  const getPage = useCallback(
    async (page: number, page_size?: number) => {
      return getContentList({
        page,
        page_size: page_size ?? paginationRef.current.page_size,
      });
    },
    [getContentList]
  );

  const getNextPage = useCallback(async () => {
    const currentPagination = paginationRef.current;
    const parsedFromNext = getPaginationParamsFromUrl(currentPagination.next);
    const totalPages = Math.ceil(currentPagination.count / currentPagination.page_size);

    const page =
      parsedFromNext.page ??
      (currentPagination.page < totalPages ? currentPagination.page + 1 : null);
    const page_size = parsedFromNext.page_size ?? currentPagination.page_size;

    if (!page) return null;

    return getContentList({ page, page_size });
  }, [getContentList]);

  const getPreviousPage = useCallback(async () => {
    const currentPagination = paginationRef.current;
    const parsedFromPrevious = getPaginationParamsFromUrl(currentPagination.previous);

    const page =
      parsedFromPrevious.page ?? (currentPagination.page > 1 ? currentPagination.page - 1 : null);
    const page_size = parsedFromPrevious.page_size ?? currentPagination.page_size;

    if (!page) return null;

    return getContentList({ page, page_size });
  }, [getContentList]);

  useEffect(() => {
    getContentList({ page: INITIAL_PAGE, page_size: INITIAL_PAGE_SIZE });
  }, [getContentList]);

  return {
    items,
    pagination,
    fetching,
    hasFetchError,
    getContentList,
    getNextPage,
    getPreviousPage,
    getPage,
  };
};

export default useCustomContentList;
